import { BLOCKS } from "@contentful/rich-text-types";
import {
   addDays,
   differenceInCalendarDays,
   differenceInSeconds,
   endOfDay,
   formatISO,
   isValid,
   min,
   parseISO,
   secondsInDay,
   secondsInHour,
   secondsInMinute,
   secondsToHours,
   secondsToMinutes,
   startOfDay
} from "date-fns";
import type { ContentfulAdventCalendarSlot, ContentfulArticle, ContentfulList } from "../../../common/types/cmsTypes";
import type { SignUpType } from "../../../common/types/promotionTypes";
import { isAdventCalendarSlot } from "../../../common/utils/cmsListUtils";

export const BEFORE_OPENING_BONUS = 10;
export const AFTER_OPENING_BONUS = 5;

export const getAdventCalendarLinkUrl = (href: string, siteBaseUrl: string) => {
   if (!siteBaseUrl || !/^https?:\/\//i.test(href)) {
      return href;
   }
   let link: URL;
   try {
      link = new URL(href);
   } catch {
      return href;
   }
   const domain = new URL(siteBaseUrl).hostname.replace(/^www\./, "");
   if (
      ![domain, `www.${domain}`, `preprod.${domain}`].includes(link.hostname) ||
      !/^\/kampanjer\/julekalender(?:\/|$)/.test(link.pathname)
   ) {
      return href;
   }
   return `${link.pathname}${link.search}${link.hash}`;
};

export type AdventCalendarCampaign = {
   year: number;
   opensAt: Date;
   reminderSignUpCode: string;
   slotIds: string[];
   slotSignUpCodes: string[];
   introduction?: string;
};

export const getAdventCalendarContest = (slot: ContentfulAdventCalendarSlot) => {
   const question = slot.fields.question;
   if (!question) {
      return;
   }
   const date = parseISO(slot.fields.date ?? "");
   if (!isValid(date)) {
      throw new Error(`Calendar door ${slot.sys.id} has a missing or invalid date.`);
   }
   const code = question.fields?.signUpCode?.trim().replace(/^julekalender-\d{4}-/, "");
   if (!code) {
      throw new Error(`The contest for calendar door ${slot.sys.id} must have a signup code.`);
   }
   if (code === "epost-paaminnelse") {
      throw new Error(`The contest for calendar door ${slot.sys.id} cannot use the reserved reminder signup code.`);
   }

   const { validFrom, validTo } = question.fields;
   const doorDate = formatISO(date, { representation: "date" });
   let dates = { validFrom: doorDate, validTo: doorDate };
   if (validFrom || validTo) {
      const from = parseISO(validFrom ?? "");
      const to = parseISO(validTo ?? "");
      const deadline = validTo?.length === 10 ? endOfDay(to) : to;
      if (!isValid(from) || !isValid(to) || deadline < from) {
         throw new Error(`The contest for calendar door ${slot.sys.id} must have a valid start and end date, in that order.`);
      }
      // Reuse the contest's duration and clock times, anchored to this door's date.
      const offset = differenceInCalendarDays(date, from);
      dates = {
         validFrom: formatISO(addDays(from, offset), { representation: validFrom?.length === 10 ? "date" : "complete" }),
         validTo: formatISO(addDays(to, offset), { representation: validTo?.length === 10 ? "date" : "complete" })
      };
   }

   return {
      ...question,
      fields: { ...question.fields, ...dates, signUpCode: `julekalender-${date.getFullYear()}-${code}` }
   };
};

export const getAdventCalendarSignUpCode = (slot: ContentfulAdventCalendarSlot) => {
   const contest = getAdventCalendarContest(slot);
   if (contest) {
      return contest.fields.signUpCode;
   }
   const date = parseISO(slot.fields.date ?? "");
   if (!isValid(date)) {
      throw new Error(`Calendar door ${slot.sys.id} has a missing or invalid date.`);
   }
   const slug = slot.fields.slug?.trim().replace(/-\d{4}$/, "");
   if (!slug || slug === "epost-paaminnelse") {
      throw new Error(`Calendar door ${slot.sys.id} must have a slug that is not the reserved reminder code.`);
   }
   return `julekalender-${date.getFullYear()}-${slug}`;
};

export const getAdventCalendarSignupButtonCode = (promotionId: string, slot?: ContentfulAdventCalendarSlot) => {
   if (!slot || !/^julekalender-\d{4}-(?!epost-paaminnelse$)/.test(promotionId)) {
      return promotionId;
   }
   if (slot.fields.question) {
      return;
   }
   return getAdventCalendarSignUpCode(slot);
};

// Only use doors linked from the published calendar article. Old, published doors can remain in the CMS.
export const getAdventCalendarCampaign = (article: ContentfulArticle): AdventCalendarCampaign => {
   const linkedSlots = article.fields.body.content.flatMap((block) => {
      if (block.nodeType !== BLOCKS.EMBEDDED_ENTRY) {
         return [];
      }
      const entry = block.data.target;
      if (entry?.sys?.contentType?.sys?.id === "adventCalendarSlot") {
         const { date, question, slotImage } = (entry as ContentfulAdventCalendarSlot).fields;
         if (date || question || slotImage) {
            throw new Error("Link calendar doors through a calendar list, not directly in the article body.");
         }
         return [];
      }
      if (entry?.sys?.contentType?.sys?.id === "callToActionList") {
         return ((entry as ContentfulList).fields.contentList ?? []).filter(isAdventCalendarSlot);
      }
      return [];
   });
   const slots = [...new Map(linkedSlots.filter((slot) => slot.fields.slotImage).map((slot) => [slot.sys.id, slot])).values()];
   if (slots.length === 0) {
      throw new Error("The calendar article must link to at least one published calendar door with an image.");
   }

   const dates = slots.map((slot) => {
      const date = parseISO(slot.fields.date ?? "");
      if (!isValid(date)) {
         throw new Error(`Calendar door ${slot.sys.id} has a missing or invalid date.`);
      }
      return startOfDay(date);
   });
   const year = dates[0].getFullYear();
   if (dates.some((date) => date.getFullYear() !== year)) {
      throw new Error("All doors linked from the calendar article must have dates in the same year.");
   }
   const reminderSignUpCode = `julekalender-${year}-epost-paaminnelse`;
   const slotSignUpCodes = slots.map(getAdventCalendarSignUpCode);
   if (new Set(slotSignUpCodes).size !== slotSignUpCodes.length) {
      throw new Error("Each calendar door must have a different signup code.");
   }

   return {
      year,
      opensAt: min(dates),
      reminderSignUpCode,
      slotIds: slots.map((slot) => slot.sys.id),
      slotSignUpCodes,
      introduction: article.fields.preface
   };
};

export const getAdventCalendarTickets = (campaign: AdventCalendarCampaign, signUps: SignUpType[]) => {
   const codes = new Set(campaign.slotSignUpCodes);
   const slotTickets = new Set(signUps.filter((signUp) => codes.has(signUp.signUpCode)).map((signUp) => signUp.signUpCode)).size;
   const reminder = signUps.find((signUp) => signUp.signUpCode === campaign.reminderSignUpCode);
   const bonusTickets = reminder
      ? new Date(reminder.created) < campaign.opensAt
         ? BEFORE_OPENING_BONUS
         : AFTER_OPENING_BONUS
      : 0;
   return { slotTickets, bonusTickets, total: slotTickets + bonusTickets, hasReminder: !!reminder };
};

export const getCalendarTimeRemaining = (opensAt: Date, now = new Date()) => {
   const seconds = Math.max(0, differenceInSeconds(opensAt, now));
   return {
      days: Math.floor(seconds / secondsInDay),
      hours: secondsToHours(seconds % secondsInDay),
      minutes: secondsToMinutes(seconds % secondsInHour),
      seconds: seconds % secondsInMinute
   };
};
